import { ROTransaction, RWTransaction } from '../../../db'
import { Context, EntityId, Id, Json, Order, PaginatedResult, Paging } from '../../../common'

export enum ModelEventStatus {
  // примененное событие в базу
  APPLIED = 'applied',
  // отложенное событие для последующей обработки
  PENDING = 'pending',
  // конфликтует с другим событием
  CONFLICT = 'conflict',
  // ошибка обработки, возможен повтор обработки позже
  ERROR = 'error',
  // мертвое событие, лимит по количеству повторных обработок исчерпан
  DEAD = 'dead',
}

export interface ModelEvent {
  id: Id
  prevEventIds?: Id[]
  entityId: EntityId
  type: string
  createdAt: Date
  storeVersion?: number
  municipalEntityId?: Id
  status: ModelEventStatus
  retryCount: number
  userId?: Id
  payload?: Json
}

export function isMergeEvent(e: ModelEvent): boolean {
  return (e.prevEventIds?.length ?? 0) > 1
}

/** Combining conditions with AND operation */
export interface ModelEventsFilter {
  ids?: Id[]
  /** Inclusive */
  fromVersion?: number
  /** Inclusive */
  toVersion?: number
  /** Common event or specific to selected municipal entity */
  commonOrSpecificMEId?: Id
  /** The target event must contain one of provided ids in its prevEventIds list */
  prevEventIds?: Id[]
  noPrevEvents?: boolean
  entityId?: Id
  hasStoreVersion?: boolean
  statuses?: ModelEventStatus[]
}

export enum ModelEventsOrderFields {
  VERSION = 'version',
  VERSION_AND_CREATED_AT = 'versionAndCreatedAt',
  CREATED_AT = 'createdAt',
}

export interface ModelEventsFindOptions {
  filter?: ModelEventsFilter
  order?: Order<ModelEventsOrderFields> | undefined
  paging?: Paging
}

export interface StoreVersion {
  id: number
  createdAt: Date
}

export interface EntityEvent {
  entityId: Id
  eventId: Id
}

export type ModelEventCountByStatus = Record<ModelEventStatus, number | undefined>

export interface ModelEventsRepository {
  findById(tx: ROTransaction, id: Id): Promise<ModelEvent | undefined>
  count(tx: ROTransaction, filter?: ModelEventsFilter): Promise<number>
  countByStatus(tx: ROTransaction, filter?: ModelEventsFilter): Promise<ModelEventCountByStatus>
  findAll(tx: ROTransaction, opts?: ModelEventsFindOptions): Promise<PaginatedResult<ModelEvent>>
  save(tx: RWTransaction, events: ModelEvent[]): Promise<void>
  filterOutExisting(tx: ROTransaction, eventIds: Id[]): Promise<Id[]>
  deleteAll(tx: RWTransaction): Promise<void>
  setEventIdForEntities(tx: RWTransaction, entityEvents: EntityEvent[]): Promise<void>
  findEventIdByEntity(tx: ROTransaction, entityId: Id): Promise<Id | undefined>
}

export interface StoreVersionsRepository {
  getStoreId(tx: ROTransaction): Promise<Id | undefined>
  setStoreId(tx: RWTransaction, id: Id): Promise<void>
  getClusterId(tx: ROTransaction): Promise<Id | undefined>
  setClusterId(tx: RWTransaction, id: Id): Promise<void>
  getNew(tx: RWTransaction): Promise<StoreVersion>
  getCurrent(tx: ROTransaction): Promise<StoreVersion | undefined>
  getFirstFromDate(tx: ROTransaction, date: Date): Promise<StoreVersion | undefined>
  getLastBeforeDate(tx: ROTransaction, date: Date): Promise<StoreVersion | undefined>
}

/** Processes events of some entity */
export interface ModelEventsProcessor {
  readonly supportedEventTypes: string[]
  createMergeEvent(ctx: Context, tx: ROTransaction, entityId: Id): Promise<ModelEvent>
  process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean>
}

export interface ProcessEventsResult {
  storeVersion: number
}

export enum CreateEventMode {
  NEW,
  UPDATE,
  NEW_OR_UPDATE,
}

export interface CreateEventOpts {
  type: string
  mode: CreateEventMode
  entityId: EntityId
  municipalEntityId?: Id
  payload?: Json
}

/** Processes all incoming events by dispatching them to the proper event processor */
export interface ModelEventsDispatcher {
  registerProcessor(processor: ModelEventsProcessor): void
  processEvents(ctx: Context, events: ModelEvent[]): Promise<ProcessEventsResult>
  processPendingEvents(ctx: Context): Promise<void>
}

export interface ModelEventCreator {
  create(ctx: Context, tx: ROTransaction, opts: CreateEventOpts): Promise<ModelEvent>
}

/**
 * Info about current local storage
 */
export interface StorageInfo {
  /** Storage identifier */
  id: Id
  clusterId?: Id
  /** Current storage version */
  currentVersion: number | undefined
}

/**
 * Describes info about remote storage
 */
export interface RemoteStorageInfo {
  /** Storage identifier */
  id: Id
  /** Global id for the whole distributed data base. Databases with different cluster id is not compatible amongst
   * each other */
  clusterId: Id
  /** Latest version of remote storage at sync time */
  latestRemoteVersion?: number
  /** Latest version of local storage at sync time */
  latestLocalVersion?: number
  /** Latest sync date and time */
  // lastSyncAt?: Date
}

export interface UpdateRemoteStorageInfo {
  clusterId?: Id
  /** Latest version of remote storage at sync time */
  latestRemoteVersion?: number
  /** Latest version of local storage at sync time */
  latestLocalVersion?: number
  /** Latest sync date and time */
  // lastSyncAt?: Date
}

/**
 * Contains info about all known remote storages. The info is only accumulated and cannot be deleted.
 */
export interface StoragesInfoRepository {
  findAll(tx: ROTransaction): Promise<RemoteStorageInfo[]>
  findById(tx: ROTransaction, id: Id): Promise<RemoteStorageInfo | undefined>
  update(tx: RWTransaction, storageId: Id, info: UpdateRemoteStorageInfo): Promise<void>
}

export interface ImportResult {
  /** Version of storage after import */
  version: number
}

export interface ModelEventsSourceServiceStat {
  getStorageInfoRequests: number
  countRequests: number
  exportRequests: number
  importRequests: number
  filterOutExistingRequests: number
}

export interface ModelEventsSourceService {
  storageName: string

  /** Get self storage info */
  getStorageInfo(ctx: Context): Promise<StorageInfo>

  /** Set cluster id */
  setClusterId(ctx: Context, id: Id): Promise<void>

  /** Count events for export */
  count(ctx: Context, filter?: ModelEventsFilter): Promise<number>

  /** Get events from storage for exporting */
  export(ctx: Context, opts?: ModelEventsFindOptions): Promise<PaginatedResult<ModelEvent>>

  /** Import new events to storage */
  import(ctx: Context, events: ModelEvent[]): Promise<ImportResult>

  /** Filter out existing event ids */
  filterOutExisting(ctx: Context, eventIds: Id[]): Promise<Id[]>
}
